/**
 * XPAnimationPortal - Global portal for XP animations
 * Renders animations at document.body level so they persist even when source component unmounts
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './XPAnimation.css';

// Global animation queue
let animationQueue = [];
let listeners = [];

// Subscribe to animation events
const subscribe = (listener) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

// Trigger animation from anywhere
export const triggerXPAnimation = (amount, position) => {
  // Don't show animation for negative XP
  if (amount <= 0) return;

  const id = Date.now() + Math.random();
  const animation = { id, amount, position, show: true };
  animationQueue.push(animation);
  listeners.forEach(listener => listener([...animationQueue]));

  // Auto-remove after animation duration (980ms = 0.98 seconds)
  setTimeout(() => {
    animationQueue = animationQueue.filter(a => a.id !== id);
    listeners.forEach(listener => listener([...animationQueue]));
  }, 980);
};

export default function XPAnimationPortal() {
  const [animations, setAnimations] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribe(setAnimations);
    return unsubscribe;
  }, []);

  return createPortal(
    <AnimatePresence>
      {animations.map((anim) => {
        const isPositive = anim.amount > 0;
        const displayAmount = isPositive ? `+${anim.amount}` : anim.amount;

        return (
          <motion.div
            key={anim.id}
            className={`xp-animation ${isPositive ? 'positive' : 'negative'}`}
            style={{
              left: anim.position.x,
              top: anim.position.y - 30, // Position well above button (40px = tooltip height + arrow + gap)
            }}
            initial={{ opacity: 0, x: '-50%', y: 0 }}
            animate={{
              opacity: [0, 1, 1, 0],
              x: '-50%',
              y: [0, -6, -12, -16], // 16px upward movement
            }}
            transition={{
              duration: 0.78,
              times: [0, 0.15, 0.5, 1],
              ease: 'easeOut',
            }}
            exit={{ opacity: 0 }}
          >
            <div className="xp-tooltip">
              {displayAmount} XP
            </div>
          </motion.div>
        );
      })}
    </AnimatePresence>,
    document.body
  );
}

